import { useState, useMemo } from 'react';
import { ModpackNote } from '../types/modpackNote';
import { MarkdownRenderer } from './MarkdownRenderer';
import { CreatorProfile } from './CreatorProfile';
import { EditModpackNoteModal } from './EditModpackNoteModal';
import { ConfirmationModal } from './ConfirmationModal';
import { modpackNotesApiService } from '../services/modpackNotesApi';
import { useAuth } from '../contexts/AuthContext';
import {
  X,
  Server,
  Globe,
  Pencil,
  Trash2,
  Clock,
  Calendar,
  Code,
  Copy,
  Check,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

interface ModpackNoteDetailModalProps {
  note: ModpackNote;
  isOpen: boolean;
  clusterThumbnails: Record<string, string>;
  onClose: () => void;
  onUpdate: (updatedNote: ModpackNote) => void;
  onDelete: (noteId: string) => void;
}

export const ModpackNoteDetailModal = ({
  note,
  isOpen,
  clusterThumbnails,
  onClose,
  onUpdate,
  onDelete,
}: ModpackNoteDetailModalProps) => {
  const { identity } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  const isDev = import.meta.env.DEV;

  const canEdit = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('argus_modpack_edit') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  const canDelete = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('argus_modpack_delete') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await modpackNotesApiService.deleteNote(note.id);
      onDelete(note.id);
      onClose();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete note');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(note.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const thumbnailUrl = note.cluster_id ? clusterThumbnails[note.cluster_id] : undefined;

  // Try to pretty-print metadata
  let formattedMetadata: string | null = null;
  if (note.metadata) {
    try {
      formattedMetadata = JSON.stringify(JSON.parse(note.metadata), null, 2);
    } catch {
      formattedMetadata = note.metadata;
    }
  }

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="modal-overlay z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="modal-panel max-w-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                {/* Association Badges */}
                <div className="flex items-center flex-wrap gap-2 mb-3">
                  {note.cluster_id && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-purple-500/15 text-purple-300 border border-purple-500/25">
                      {thumbnailUrl ? (
                        <img
                          src={thumbnailUrl}
                          alt={note.cluster_id}
                          className="w-5 h-5 rounded-sm object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <Server className="w-4 h-4" />
                      )}
                      {note.cluster_id}
                    </span>
                  )}
                  {note.fqdn && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-accent-500/15 text-accent-300 border border-accent-500/25">
                      <Globe className="w-4 h-4" />
                      {note.fqdn}
                    </span>
                  )}
                </div>

                {/* Note ID */}
                <button
                  onClick={handleCopyId}
                  className="text-xs text-glass-500 font-mono hover:text-glass-300 transition-colors flex items-center gap-1"
                  title="Copy note ID"
                >
                  {copiedId ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                  {note.id}
                </button>
              </div>

              <div className="flex items-center gap-2 ml-4">
                {canEdit && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="glass-button-secondary px-3 py-1.5"
                  >
                    <Pencil className="w-4 h-4 mr-1.5" />
                    Edit
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="glass-button-secondary px-3 py-1.5 hover:bg-red-500/15 hover:text-red-400 hover:border-red-500/25"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" />
                    Delete
                  </button>
                )}
                <button onClick={onClose} className="glass-button p-2 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Timestamps */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-glass-400 mb-5 pb-4 border-b border-glass-200/10">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                <span>Created {format(new Date(note.created_at), 'MMM d, yyyy \'at\' h:mm a')}</span>
                <span className="text-glass-500">({formatDistanceToNow(new Date(note.created_at), { addSuffix: true })})</span>
              </div>
              {note.updated_at && (
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Edited {formatDistanceToNow(new Date(note.updated_at), { addSuffix: true })}</span>
                  {note.updated_by && (
                    <>
                      <span>by</span>
                      <CreatorProfile creatorId={note.updated_by} inline />
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="mb-5">
              <MarkdownRenderer content={note.content} />
            </div>

            {/* Metadata */}
            {formattedMetadata && (
              <div className="mb-5 border-t border-glass-200/10 pt-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-glass-300 mb-2">
                  <Code className="w-4 h-4" />
                  Metadata
                </div>
                <pre className="bg-dark-900/50 rounded-lg p-4 text-xs text-glass-300 overflow-x-auto font-mono">
                  {formattedMetadata}
                </pre>
              </div>
            )}

            {/* Creator */}
            <div className="border-t border-glass-200/10 pt-4">
              <div className="text-xs text-glass-500 mb-1">Created by</div>
              <CreatorProfile creatorId={note.creator_id} />
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Edit Modal */}
      {isEditing && (
        <EditModpackNoteModal
          note={note}
          onClose={() => setIsEditing(false)}
          onSave={(updatedNote) => {
            onUpdate(updatedNote);
            setIsEditing(false);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete Modpack Note"
          message="Are you sure you want to permanently delete this modpack note? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          isLoading={isDeleting}
        />
      )}
    </>
  );
};
